import { ArrowDownRight, ArrowUpRight, Check } from 'lucide-react'
import { Link } from 'react-router-dom'
import { LoadingState } from './States'
import './StatCard.css'

/**
 * StatCard — KPI / metric card for dashboards.
 *
 * Ported from the rawam-ui reference panel so the two apps carry one card:
 * same markup, same class names, same tone contract. A surface card showing a
 * label, a large value, an optional leading icon chip, an optional delta/trend,
 * an optional muted hint, and an optional footer slot. Purely presentational —
 * callers pass already-formatted strings/nodes.
 *
 * Renders as a <button> when `onClick` is given, a <Link> when `to` is given
 * (this app's lists point at their own detail pages), otherwise a plain <div>.
 *
 * @param {import('react').ReactNode} label  the metric name
 * @param {import('react').ReactNode} value  the primary figure, already formatted
 * @param {import('react').ElementType} [icon]  rendered in a tinted leading chip
 * @param {'neutral'|'accent'|'success'|'warning'|'danger'|'info'|'gold'|'violet'
 *   |'rose'} [tone]  tints the icon chip, and washes the card faintly behind it.
 *   `gold` is CEREMONIAL, not informational: identity moments, never a warning —
 *   that is what `warning` is for, and the two read identically at chip size if
 *   gold is spent on state. `rose` means NOTHING: it exists so a strip of six
 *   figures can be six colours without inventing six claims.
 *
 *   `violet` IS THE "ALL" SCOPE, on every strip in the panel — all groups, all
 *   surveys, all policies, all content, all invitations. It used to fall out of
 *   `spreadTones` by luck, so the one card that means the same thing on every
 *   screen was the one card that never looked the same. Reach for another tone
 *   for anything that is not "no filter applied".
 * @param {{value: import('react').ReactNode, direction: 'up'|'down', tone?: string}} [delta]
 * @param {import('react').ReactNode} [hint]  small muted caption under the value
 * @param {boolean} [quiet]  the metric is zero / has nothing to report. Mutes the
 *   figure and drops the chip to neutral, so a strip reading "0 · 0 · 0%" stops
 *   shouting as loudly as the cards carrying real signal. Still fully legible —
 *   this is emphasis, not disablement.
 * @param {boolean} [loading]  inline spinner in place of the value
 * @param {boolean} [active]   this card is the filter currently applied
 * @param {string} [to]        navigate on click
 */
const TONES = [
  'neutral',
  'accent',
  'success',
  'warning',
  'danger',
  'info',
  'gold',
  'violet',
  'rose',
]

export default function StatCard({
  label,
  value,
  icon: Icon,
  tone = 'accent',
  delta,
  hint,
  quiet = false,
  loading = false,
  disabled = false,
  active = false,
  /**
   * A FIGURE IN A STRIP OF CONTROLS. Set by `StatStrip` on a card that cannot be pressed
   * while its neighbours can — it drops the tile and the tone so the reader can see, at
   * rest and without hovering, which cards do something. A strip where NOTHING is
   * pressable does not set it: there is no contrast to draw, and a page of readouts
   * should still look like cards.
   */
  readout = false,
  to,
  onClick,
  children,
  className = '',
  ...rest
}) {
  const safeTone = TONES.includes(tone) ? tone : 'accent'
  const interactive = typeof onClick === 'function' || Boolean(to)

  const classes = [
    'stat-card',
    `stat-card--tone-${safeTone}`,
    interactive && 'stat-card--interactive',
    /* A card you cannot press should not look like one you can. The static
       variant sits a step smaller and flatter, so a strip that mixes filter
       cards with plain readouts says which is which before you hover it. */
    !interactive && 'stat-card--static',
    !interactive && readout && 'stat-card--readout',
    quiet && !loading && 'stat-card--quiet',
    active && 'is-active-filter',
    loading && 'is-loading',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  // Derive the delta tone: explicit override wins, else map direction.
  const deltaTone =
    delta && TONES.includes(delta.tone)
      ? delta.tone
      : delta?.direction === 'down'
        ? 'danger'
        : 'success'
  const DeltaArrow = delta?.direction === 'down' ? ArrowDownRight : ArrowUpRight

  const content = (
    <>
      <div className="stat-card__row">
        {Icon ? (
          <span className="stat-card__chip" aria-hidden="true">
            <Icon size={20} strokeWidth={2} />
          </span>
        ) : null}

        <div className="stat-card__content">
          <span className="stat-card__label">
            <bdi>{label}</bdi>
          </span>
          {loading ? (
            <div className="stat-card__loading">
              <LoadingState inline label="" />
            </div>
          ) : (
            <div className="stat-card__metric">
              <span className="stat-card__value">
                <bdi>{value}</bdi>
              </span>
              {delta ? (
                <span className={`stat-card__delta stat-card__delta--tone-${deltaTone}`}>
                  <DeltaArrow
                    className="stat-card__delta-arrow rtl-flip"
                    size={16}
                    strokeWidth={2.25}
                    aria-hidden="true"
                  />
                  <span className="stat-card__delta-value">{delta.value}</span>
                </span>
              ) : null}
            </div>
          )}
          {hint && !loading ? (
            <span className="stat-card__hint">
              <bdi>{hint}</bdi>
            </span>
          ) : null}
        </div>

        {/* THE CUE: what pressing the card does, readable at rest (2026-10-19). A
            filter wears a selection ring that fills with a check while it is the
            filter applied; a link wears a forward arrow. A readout wears nothing. */}
        {to ? (
          <span className="stat-card__cue stat-card__cue--go" aria-hidden="true">
            <ArrowUpRight className="rtl-flip" size={16} strokeWidth={2} />
          </span>
        ) : interactive ? (
          <span className="stat-card__cue stat-card__cue--pick" aria-hidden="true">
            <Check size={10} strokeWidth={3.5} />
          </span>
        ) : null}
      </div>

      {children ? <div className="stat-card__footer">{children}</div> : null}
    </>
  )

  if (to) {
    return (
      <Link className={classes} to={to} {...rest} data-testid="stat-tile">
        {content}
      </Link>
    )
  }

  if (typeof onClick === 'function') {
    return (
      <button
        type="button"
        className={classes}
        onClick={onClick}
        // While loading the value is a spinner, so the action isn't meaningful
        // yet — block activation and announce the busy state.
        disabled={disabled || loading}
        aria-pressed={active}
        aria-busy={loading || undefined}
        {...rest}
        data-testid="stat-tile"
      >
        {content}
      </button>
    )
  }

  return (
    <div className={classes} {...rest} data-testid="stat-tile">
      {content}
    </div>
  )
}
